import * as React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useMutation } from '@apollo/client';

import { useRetracked } from 'js/lib/retracked';

import type { CoachInteractionType } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import FeedbackModal from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import { AICOACH_FEEDBACK, MESSAGE_SENDER } from 'bundles/ai-coach-platform/components/patterns/chat/constants';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import { setLike, setUnLike, setUnSet } from 'bundles/ai-coach-platform/components/patterns/chat/store/actions';
import type { MessageTypes } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import { getCookieId, getDeviceId } from 'bundles/ai-coach-platform/components/patterns/chat/utils/logoutModeUtils';
import type {
  AiCoachPlatform_UpdateWithUserFeedbackMutation as UpdateWithUserFeedbackMutation,
  AiCoachPlatform_UpdateWithUserFeedbackMutationVariables as UpdateWithUserFeedbackMutationVariables,
} from 'bundles/ai-coach-platform/queries/__generated__/aiCoachUpdateWithUserFeedbackMutation';
import AiCoachUpdateWithUserFeedbackMutation from 'bundles/ai-coach-platform/queries/aiCoachUpdateWithUserFeedbackMutation.graphql';
import { captureMessageInSentry } from 'bundles/ai-coach-platform/utils/sentry';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

import _t from 'i18n!nls/ai-coach-platform';

type FeedBackActions = (typeof AICOACH_FEEDBACK)[keyof typeof AICOACH_FEEDBACK];

type UseFeedbackProps = {
  message: MessageTypes;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
};

type UseFeedbackResult = {
  onThumbsUp: (() => void) | undefined;
  onThumbsDown: (() => void) | undefined;
  thumbsUpSelected: boolean;
  thumbsDownSelected: boolean;
  feedbackModal: React.ReactNode;
};

const useFeedback = ({ message, feedbackIssueOptions, onFeedbackSubmitted }: UseFeedbackProps): UseFeedbackResult => {
  const dispatch = useDispatch();
  const trackRetracked = useRetracked();
  const { enableLoggedOutMode } = useCoachChatContext();
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const cookieId = useMemo(() => (enableLoggedOutMode ? getCookieId() : undefined), [enableLoggedOutMode]);
  const deviceId = useMemo(() => (enableLoggedOutMode ? getDeviceId() : undefined), [enableLoggedOutMode]);

  const [feedbackGiven, setFeedbackGiven] = React.useState<FeedBackActions>(
    message.userFeedback ?? AICOACH_FEEDBACK.unset
  );
  // Tracks whether the modal should be visible. Intentionally NOT initialised from
  // message.userFeedback so the modal does not reopen on page reload.
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);

  const [updateWithUserFeedbackInput] = useMutation<
    UpdateWithUserFeedbackMutation,
    UpdateWithUserFeedbackMutationVariables
  >(AiCoachUpdateWithUserFeedbackMutation, { context: { clientName: 'gatewayGql' } });

  const { customId: messageCustomId } = message;

  const handleTracking = React.useCallback(
    (trackingName: string, interaction?: CoachInteractionType) => {
      trackRetracked({
        trackingData: {
          message_id: messageCustomId,
          coach_response: message.text,
          timestamp: Date.now(),
        },
        trackingName,
        action: 'click',
      });

      if (interaction && trackingContext) {
        track('interact_coach', {
          coachInteractionType: interaction,
          coach: { mode: trackingContext.mode },
          ...(trackingContext.courseId && { product: { type: 'course', id: trackingContext.courseId as string } }),
          ...(trackingContext.itemId && {
            courseItem: {
              id: trackingContext.itemId as string,
              ...(trackingContext.itemType && { type: trackingContext.itemType }),
            },
          }),
        });
      }
    },
    [trackRetracked, messageCustomId, message.text, track, trackingContext]
  );

  const mutate = React.useCallback(
    (userFeedback: FeedBackActions, userComments?: string) => {
      if (!messageCustomId) return;
      updateWithUserFeedbackInput({
        variables: {
          input: {
            messageId: messageCustomId,
            userComments,
            userFeedback,
            ...(cookieId && { cookieId }),
            ...(deviceId && { deviceId }),
          },
        },
      }).catch((error: Error) => {
        captureMessageInSentry({
          ...(trackingContext && { coachMode: trackingContext.mode }),
          error,
          source: 'useFeedback_mutate',
          extra: { messageId: messageCustomId },
        });
      });
    },
    [messageCustomId, updateWithUserFeedbackInput, cookieId, deviceId, trackingContext]
  );

  const onThumbsUp = React.useCallback(() => {
    const isDeselect = feedbackGiven === AICOACH_FEEDBACK.like;
    if (isDeselect) {
      handleTracking('ai_course_coach_chat_user_feedback_remove_selection');
      setFeedbackGiven(AICOACH_FEEDBACK.unset);
      setShowFeedbackModal(false);
      dispatch(setUnSet(messageCustomId!));
      mutate(AICOACH_FEEDBACK.unset);
    } else {
      handleTracking('ai_course_coach_chat_user_feedback_like_select', 'like_message');
      setFeedbackGiven(AICOACH_FEEDBACK.like);
      setShowFeedbackModal(true);
      dispatch(setLike(messageCustomId!));
      mutate(AICOACH_FEEDBACK.like);
    }
  }, [feedbackGiven, handleTracking, dispatch, messageCustomId, mutate]);

  const onThumbsDown = React.useCallback(() => {
    const isDeselect = feedbackGiven === AICOACH_FEEDBACK.dislike;
    if (isDeselect) {
      handleTracking('ai_course_coach_chat_user_feedback_remove_selection');
      setFeedbackGiven(AICOACH_FEEDBACK.unset);
      setShowFeedbackModal(false);
      dispatch(setUnSet(messageCustomId!));
      mutate(AICOACH_FEEDBACK.unset);
    } else {
      handleTracking('ai_course_coach_chat_user_feedback_dislike_select', 'dislike_message');
      setFeedbackGiven(AICOACH_FEEDBACK.dislike);
      setShowFeedbackModal(true);
      dispatch(setUnLike(messageCustomId!));
      mutate(AICOACH_FEEDBACK.dislike);
    }
  }, [feedbackGiven, handleTracking, dispatch, messageCustomId, mutate]);

  let feedbackPlaceholderText = '';
  if (feedbackGiven === AICOACH_FEEDBACK.like) {
    feedbackPlaceholderText = _t('What do you like about this response?');
  } else if (feedbackGiven === AICOACH_FEEDBACK.dislike) {
    feedbackPlaceholderText = _t('How could this response be improved?');
  }

  const feedbackModal = showFeedbackModal ? (
    <FeedbackModal
      key={feedbackGiven}
      title={feedbackPlaceholderText}
      issueOptions={feedbackGiven === AICOACH_FEEDBACK.dislike ? feedbackIssueOptions : undefined}
      onSubmit={({ userComments, userIssue }) => {
        if (userComments) mutate(feedbackGiven, userComments);
        if (userIssue) onFeedbackSubmitted?.({ messageId: messageCustomId!, userIssue, userComments });
        handleTracking('ai_course_coach_chat_user_feedback_feedback_form_submit');
      }}
      onClose={() => handleTracking('ai_course_coach_chat_user_feedback_feedback_form_close')}
      onCancel={() => handleTracking('ai_course_coach_chat_user_feedback_feedback_form_cancel')}
    />
  ) : null;

  // Only available for server-sent messages with a customId
  if (!messageCustomId || message.sender !== MESSAGE_SENDER.RESPONSE) {
    return {
      onThumbsUp: undefined,
      onThumbsDown: undefined,
      thumbsUpSelected: false,
      thumbsDownSelected: false,
      feedbackModal: null,
    };
  }

  return {
    onThumbsUp,
    onThumbsDown,
    thumbsUpSelected: message.userFeedback === AICOACH_FEEDBACK.like,
    thumbsDownSelected: message.userFeedback === AICOACH_FEEDBACK.dislike,
    feedbackModal,
  };
};

export default useFeedback;
